import {useCallback} from 'react';
import {
    setHiddenWall,
    setIsCreatingSnapshots,
    setIsWireframeMode,
    setPreviewPosition,
    setShowArrows,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useAppDispatch} from 'store/customer';
import * as THREE from 'three';
import {SnapshotLocations} from '../constants/SnapshotLocations';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {
    applySnapshotLabelScales,
    restoreSnapshotLabelScales,
    SNAPSHOT_RENDER_HEIGHT,
    SNAPSHOT_RENDER_WIDTH,
} from './snapshotLabelScale';
import {getOrthoFrustumForBox} from './fitSnapshotCamera';
import {waitForPendingTextures} from 'components/customer/Preview3D/helpers/Three';

// High resolution to RENDER
const RENDER_WIDTH = SNAPSHOT_RENDER_WIDTH;
const RENDER_HEIGHT = SNAPSHOT_RENDER_HEIGHT;

// Output size you want the final image to be
const OUTPUT_WIDTH = 1024;
const OUTPUT_HEIGHT = 550;

const EXPORT_ASPECT = OUTPUT_WIDTH / OUTPUT_HEIGHT;

const isAngledSnapshot = (saveto: keyof typeof SnapshotLocations) =>
    saveto === 'TOP_LEFT_VIEW' || saveto === 'TOP_RIGHT_VIEW';

const applyOrthoFrustum = (
    camera: THREE.OrthographicCamera,
    frustum: ReturnType<typeof getOrthoFrustumForBox>
) => {
    camera.left = frustum.left;
    camera.right = frustum.right;
    camera.top = frustum.top;
    camera.bottom = frustum.bottom;
    camera.zoom = 1;
    camera.updateProjectionMatrix();
};

const fitCameraToWorldBox = (
    camera: THREE.OrthographicCamera,
    box: THREE.Box3,
    padding: number
) => {
    if (box.isEmpty()) {
        return;
    }

    camera.updateMatrixWorld(true);

    const corners = [
        new THREE.Vector3(box.min.x, box.min.y, box.min.z),
        new THREE.Vector3(box.min.x, box.min.y, box.max.z),
        new THREE.Vector3(box.min.x, box.max.y, box.min.z),
        new THREE.Vector3(box.min.x, box.max.y, box.max.z),
        new THREE.Vector3(box.max.x, box.min.y, box.min.z),
        new THREE.Vector3(box.max.x, box.min.y, box.max.z),
        new THREE.Vector3(box.max.x, box.max.y, box.min.z),
        new THREE.Vector3(box.max.x, box.max.y, box.max.z),
    ];

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const corner of corners) {
        corner.applyMatrix4(camera.matrixWorldInverse);
        minX = Math.min(minX, corner.x);
        maxX = Math.max(maxX, corner.x);
        minY = Math.min(minY, corner.y);
        maxY = Math.max(maxY, corner.y);
    }

    applyOrthoFrustum(
        camera,
        getOrthoFrustumForBox(minX, maxX, minY, maxY, EXPORT_ASPECT, padding)
    );
};

/**
 * Bounding box of everything currently visible in the room. Since the
 * snapshot loop toggles exactly the active stage's measurement groups and
 * cabinets visible before capture, fitting to the visible box guarantees no
 * measurement label gets cropped off the shot.
 * @param {THREE.Object3D} roomRoot - Root wall group of the room
 * @return {THREE.Box3} Bounding box of the visible room contents
 */
const getVisibleRoomBox = (roomRoot: THREE.Object3D) => {
    const box = new THREE.Box3();
    const spriteWorld = new THREE.Vector3();
    roomRoot.updateMatrixWorld(true);
    roomRoot.traverse((object) => {
        // Hidden objects (other print views' groups, excluded cabinets,
        // dimension arrows) must not stretch the camera fit.
        let invisible = !object.visible;
        object.traverseAncestors((ancestor) => {
            if (!ancestor.visible) {
                invisible = true;
            }
        });
        if (invisible) {
            return;
        }
        if (object instanceof THREE.Sprite) {
            // Expand by the label's extent, not just its centre, so labels
            // on the outer rows aren't half-cropped. Rotated elevation
            // height labels swap the quad axes in screen space.
            object.getWorldPosition(spriteWorld);
            const spriteScale = object.getWorldScale(new THREE.Vector3());
            const material = object.material;
            const rotation = Math.abs(material?.rotation ?? 0);
            const swapped =
                Math.abs(rotation - Math.PI / 2) < 0.2 ||
                Math.abs(rotation - Math.PI * 1.5) < 0.2;
            const halfExtent = new THREE.Vector3(
                Math.abs(swapped ? spriteScale.y : spriteScale.x) / 2,
                Math.abs(swapped ? spriteScale.x : spriteScale.y) / 2,
                Math.abs(spriteScale.x) / 2
            );
            box.expandByPoint(spriteWorld.clone().add(halfExtent));
            box.expandByPoint(spriteWorld.clone().sub(halfExtent));
            return;
        }
        if (
            !(
                object instanceof THREE.Mesh ||
                object instanceof THREE.Line ||
                object instanceof THREE.LineSegments
            ) ||
            !object.geometry
        ) {
            return;
        }
        const geometry = object.geometry as THREE.BufferGeometry;
        if (!geometry.boundingBox) {
            geometry.computeBoundingBox();
        }
        const geometryBox = geometry.boundingBox;
        if (!geometryBox) {
            return;
        }
        box.union(geometryBox.clone().applyMatrix4(object.matrixWorld));
    });
    return box;
};

const frameCameraForSnapshot = (
    scene: THREE.Scene,
    camera: THREE.OrthographicCamera,
    saveto: keyof typeof SnapshotLocations
) => {
    const roomRoot =
        scene.getObjectByName(ObjectNameMap.rectangularWall) ??
        scene.getObjectByName(ObjectNameMap.lShapeWall);
    if (!roomRoot) {
        return;
    }

    const roomBox = getVisibleRoomBox(roomRoot);
    if (roomBox.isEmpty()) {
        roomBox.setFromObject(roomRoot);
    }

    // Elevations need extra room so wall-height arrow heads are not clipped.
    const padding = isAngledSnapshot(saveto) ? 1.08 : 1.2;
    fitCameraToWorldBox(camera, roomBox, padding);
};

// Which tagged print-measurement view each snapshot stage shows. Perspective
// stages (TOP_LEFT_VIEW / TOP_RIGHT_VIEW) show none.
const PRINT_VIEW_BY_STAGE: Partial<
    Record<keyof typeof SnapshotLocations, string>
> = {
    KITCHEN_PLAN: 'TOP',
    FRONT_ELEVATION: 'FRONT',
    LEFT_ELEVATION: 'RIGHT_LEVELED',
    RIGHT_ELEVATION: 'LEFT_LEVELED',
};

const ELEVATION_STAGES: (keyof typeof SnapshotLocations)[] = [
    'FRONT_ELEVATION',
    'LEFT_ELEVATION',
    'RIGHT_ELEVATION',
];

/**
 * Measurement groups for every print view are built once when snapshot mode
 * starts (tagged with userData.printView); each stage only toggles their
 * visibility and hides the cabinets excluded from that elevation, instead of
 * tearing measurements down and rebuilding them per view.
 * @param {THREE.Scene} scene - Room planner scene
 * @param {string} saveto - Snapshot stage being captured
 * @return {void}
 */
const applyStagePrintVisibility = (
    scene: THREE.Scene,
    saveto: keyof typeof SnapshotLocations
) => {
    // eslint-disable-next-line security/detect-object-injection -- saveto is from a predefined enum, not user input
    const printView = PRINT_VIEW_BY_STAGE[saveto] ?? null;
    const excludedCabinets = new Set<THREE.Object3D>();
    let hasExcludedList = false;
    const cabinetGroups: THREE.Object3D[] = [];

    scene.traverse((object) => {
        if (object?.name?.includes('cabinet-group')) {
            cabinetGroups.push(object);
        }
        const view = object.userData?.printView as string | undefined;
        if (!view) {
            return;
        }
        const show =
            view === printView && object.userData?.printVisible !== false;
        object.visible = show;
        const excluded = object.userData?.excludedCabinets as
            THREE.Object3D[] | undefined;
        if (show && Array.isArray(excluded)) {
            hasExcludedList = true;
            excluded.forEach((cabinet) => excludedCabinets.add(cabinet));
        }
    });

    if (ELEVATION_STAGES.includes(saveto)) {
        // Elevations only show the viewed wall's cabinets. When the viewed
        // wall produced no measurement group, hide everything so the stage
        // is skipped (same as the previous per-view rebuild behaviour).
        cabinetGroups.forEach((cabinet) => {
            cabinet.visible = hasExcludedList
                ? !excludedCabinets.has(cabinet)
                : false;
        });
    } else {
        cabinetGroups.forEach((cabinet) => {
            cabinet.visible = true;
        });
    }
};

// Utility to capture screenshot
export const captureScreenshot = async (
    saveto: keyof typeof SnapshotLocations,
    scene: THREE.Scene,
    camera: THREE.OrthographicCamera,
    renderer: THREE.WebGLRenderer
): Promise<string> => {
    const originalRendererSize = new THREE.Vector2();
    renderer.getSize(originalRendererSize);
    const originalPixelRatio = renderer.getPixelRatio();
    let originalSpriteScales: ReturnType<typeof applySnapshotLabelScales> = [];
    let originalBackground: THREE.Color | THREE.Texture | null | undefined;

    const restoreCaptureView = () => {
        if (originalBackground !== undefined) {
            scene.background = originalBackground;
        }
        restoreSnapshotLabelScales(originalSpriteScales);
        originalSpriteScales = [];
        renderer.setPixelRatio(originalPixelRatio);
        renderer.setSize(originalRendererSize.x, originalRendererSize.y, false);
    };

    // Show only this stage's measurement groups and cabinets
    applyStagePrintVisibility(scene, saveto);

    let hasProduct = false;

    scene.traverse((object3d) => {
        if (object3d instanceof THREE.Sprite) {
            const dark =
                object3d.userData.measurementType === 'emptySpace'
                    ? 0.45
                    : 0.05;
            object3d.material.color.setHSL(0, 0, dark);
        }
        // hide dimensions arrows in pdf
        if (object3d?.name === 'dimension-arrows') {
            object3d.visible = false;
        }

        if (
            [ObjectNameMap.floor, ObjectNameMap.lshapeFloor].includes(
                object3d?.name
            )
        ) {
            // hide the floor to cleanup perspective views
            object3d.visible = ['TOP_LEFT_VIEW', 'TOP_RIGHT_VIEW'].includes(
                saveto
            );
        }

        if (object3d?.name?.includes('cabinet-group') && object3d.visible) {
            hasProduct = true;
        }
    });

    if (!hasProduct) {
        // do not capture anything
        return Promise.resolve('');
    }

    try {
        // Fit a clone so the live preview camera (and room size) never jumps.
        const captureCamera = camera.clone();
        captureCamera.updateMatrixWorld(true);
        frameCameraForSnapshot(scene, captureCamera, saveto);

        originalSpriteScales = applySnapshotLabelScales(
            scene,
            captureCamera,
            saveto === 'KITCHEN_PLAN'
        );

        renderer.setPixelRatio(1);
        renderer.setSize(RENDER_WIDTH, RENDER_HEIGHT, false);

        // The PDF uses a plain white background instead of the 3DRP gradient.
        originalBackground = scene.background;
        scene.background = new THREE.Color(0xffffff);

        renderer.render(scene, captureCamera);

        const dataURL = renderer.domElement.toDataURL('image/png');

        // Restore the live canvas before stashing the image so the wait
        // preview does not keep the capture buffer on screen between stages.
        restoreCaptureView();
        renderer.render(scene, camera);

        const screenshotUrl = await new Promise<string>((resolve) => {
            setTimeout(() => {
                // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined enum, not user input
                const locationKey = SnapshotLocations[saveto];
                if (locationKey) {
                    localStorage.setItem(locationKey, dataURL);
                }
                resolve(dataURL);
            }, 0);
        });

        return screenshotUrl;
    } finally {
        restoreCaptureView();
    }
};

// Hook to execute snapshot stages
export const useRoomSnapshots = () => {
    const dispatch = useAppDispatch();
    const {setSelectedCabinetGroup} = useRoomPlannerContext();

    const setTopViewPosition = () => {
        dispatch(setIsWireframeMode(true));
        dispatch(setShowArrows(true));
        dispatch(setPreviewPosition('TOP'));
    };

    const setLeftViewPosition = () => {
        dispatch(setPreviewPosition('LEFT'));
        // some cases when debugging from redux I observed this does not get triggered
        // so adding this manually to keep the top left view unblocked during report generation
        dispatch(setHiddenWall('frontWall,leftWall'));
    };
    const setRightViewPosition = () => dispatch(setPreviewPosition('RIGHT'));
    const setFrontViewPosition = () => dispatch(setPreviewPosition('FRONT'));

    const startSnapshotMode = () => dispatch(setIsCreatingSnapshots(true));

    const stopSnapshotMode = () => dispatch(setIsCreatingSnapshots(false));

    const setLeftLeveledViewPosition = () =>
        dispatch(setPreviewPosition('RIGHT_LEVELED'));
    const setRightLeveledViewPosition = () =>
        dispatch(setPreviewPosition('LEFT_LEVELED'));

    const deselectCabinet = () => setSelectedCabinetGroup(null);

    const hideArrows = () => dispatch(setShowArrows(false));

    const delay = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

    const waitForNextFrame = () =>
        new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

    const executeViewStages = useCallback(
        async (
            scene: THREE.Scene,
            camera: THREE.OrthographicCamera,
            renderer: THREE.WebGLRenderer,
            snapDelay = 500
        ) => {
            const stages: {
                name: keyof typeof SnapshotLocations;
                actions: (() => void)[];
            }[] = [
                {
                    name: 'TOP_RIGHT_VIEW',
                    actions: [setRightViewPosition],
                },
                {
                    name: 'TOP_LEFT_VIEW',
                    actions: [setLeftViewPosition],
                },
                {
                    name: 'KITCHEN_PLAN',
                    actions: [setTopViewPosition],
                },
                {
                    name: 'FRONT_ELEVATION',
                    actions: [setFrontViewPosition],
                },
                {
                    name: 'LEFT_ELEVATION',
                    actions: [setLeftLeveledViewPosition],
                },
                {
                    name: 'RIGHT_ELEVATION',
                    actions: [setRightLeveledViewPosition],
                },
            ];

            // cleanup before storing the urls
            Object.entries(SnapshotLocations).forEach(([, value]) => {
                localStorage.removeItem(value);
            });

            // initiate the start here -- ensures we have proper loader even reordering the steps
            startSnapshotMode();

            // initialise steps to speed up a bit
            hideArrows();
            deselectCabinet();

            for (const stage of stages) {
                for (const action of stage.actions) {
                    action();
                    await delay(snapDelay);
                }

                // Toggle this stage's cabinets/measurements only. Keep the
                // live camera as-is so the wait preview stays one room size
                // while views change (same as master). Capture still fits
                // its own camera for the PDF image.
                applyStagePrintVisibility(scene, stage.name);

                await waitForNextFrame();
                await delay(snapDelay);

                // Panels mount with a white placeholder and swap to their
                // textures once decoded; capture only after that has happened.
                await waitForPendingTextures();

                await captureScreenshot(stage.name, scene, camera, renderer);
            }

            // Restore cabinets hidden for elevation captures
            scene.traverse((object3d) => {
                if (object3d?.name?.includes('cabinet-group')) {
                    object3d.visible = true;
                }
            });

            // end the snapshot once all is done including the capturing of screenshot
            // this ensures we are capturing the last bit of perspective using printing state in 3DRP
            stopSnapshotMode();

            void genericPubSub.publish(
                Event.ROOM_PLANNER_SNAPSHOT_COMPLETED,
                'Report has been generated successfully!'
            );
        },
        [] // stable reference
    );

    return {
        createSnapshots: executeViewStages,
    };
};
